import { forwardRef, useEffect, useRef } from 'react';
import { cn } from '../../utils/cn';
import { CALLER_FONT_SIZE } from './fieldFontSize';

interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Label text rendered above the textarea */
  label?: string;
  /** Error message -- turns border red and shows error text below */
  error?: string;
  /** Helper text shown below the textarea (hidden when error is present) */
  helperText?: string;
  /** When true, textarea height grows with content (disables manual resize) */
  autoGrow?: boolean;
  /** Maximum character count (native + visual counter) */
  maxLength?: number;
  /** Show character count indicator (requires maxLength) */
  showCount?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea(
    {
      label,
      error,
      helperText,
      autoGrow = false,
      maxLength,
      showCount = false,
      className,
      id,
      value,
      onChange,
      ...props
    },
    ref,
  ) {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const internalRef = useRef<HTMLTextAreaElement | null>(null);

    const setRef = (el: HTMLTextAreaElement | null) => {
      internalRef.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) ref.current = el;
    };

    useEffect(() => {
      if (autoGrow && internalRef.current) {
        const el = internalRef.current;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
      }
    }, [autoGrow, value]);

    const charCount =
      typeof value === 'string' ? value.length : 0;

    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label
            htmlFor={inputId}
            className="text-sm font-medium text-text-secondary"
          >
            {label}
          </label>
        )}
        <textarea
          ref={setRef}
          id={inputId}
          value={value}
          onChange={onChange}
          maxLength={maxLength}
          className={cn(
            'w-full px-3 py-2 rounded-lg text-text-primary bg-bg-secondary min-h-[var(--touch-target-min)] lg:min-h-0',
            // >= 16px below lg (no iOS zoom on focus), 14px from lg; a caller font size replaces both
            !CALLER_FONT_SIZE.test(className ?? '') && 'text-base lg:text-sm',
            'border border-border-control outline-none transition-all duration-200',
            'placeholder:text-text-tertiary resize-y',
            'focus:border-border-focus focus:ring-1 focus:ring-accent-500/30',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            error &&
              'border-border-error focus:border-border-error focus:ring-error/30',
            autoGrow && 'resize-none overflow-hidden',
            className,
          )}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={
            error
              ? `${inputId}-error`
              : helperText
                ? `${inputId}-helper`
                : undefined
          }
          {...props}
        />
        <div className="flex items-center justify-between">
          <div>
            {error && (
              <p
                id={`${inputId}-error`}
                className="text-xs text-error-text"
                role="alert"
              >
                {error}
              </p>
            )}
            {helperText && !error && (
              <p id={`${inputId}-helper`} className="text-xs text-text-tertiary">
                {helperText}
              </p>
            )}
          </div>
          {showCount && maxLength && (
            <span className="text-xs text-text-tertiary tabular-nums">
              {charCount}/{maxLength}
            </span>
          )}
        </div>
      </div>
    );
  },
);

export default Textarea;
